import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import WindowSize from './WindowSize';
import { prefersReducedMotion } from '../utils/reducedMotion';

gsap.registerPlugin(ScrollTrigger);

let instance: Scroll;

class Scroll {
  scroll!: Lenis;
  scrollbarWidth!: number;
  hasScrollBarOnInit!: boolean;
  windowSize: WindowSize;
  private reducedMotion: boolean;

  constructor() {
    if (instance) {
      throw new Error('You can only create one instance!');
    }
    instance = this;

    this.hasScrollBarOnInit = true;
    this.windowSize = WindowSize.getInstance();
    this.scrollbarWidth = 0;
    this.reducedMotion = prefersReducedMotion();

    this.init();
  }

  init() {
    const lenis = new Lenis({
      // lerp: 1 = no smoothing (instant catch-up) under reduced motion
      lerp: this.reducedMotion ? 1 : 0.25,
      prevent: (node) =>
        node.id === 'header-nav' ||
        Boolean(node.closest?.('.cky-preference-body-wrapper, .cky-modal')),
    });

    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    this.scroll = lenis;

    this.onResize();
    this.windowSize.addListener(this.onResize.bind(this));
  }

  onResize() {
    this.getScrollbarWidth();
    this.checkIfPageHasScrollbar();
  }

  checkIfPageHasScrollbar() {
    this.hasScrollBarOnInit = document.body.scrollHeight > window.innerHeight;
  }

  disableScrolling() {
    this.scroll.stop();

    document.documentElement.style.setProperty(
      '--scrollbar-width',
      `${this.hasScrollBarOnInit ? this.scrollbarWidth : 0}px`,
    );
  }

  enableScrolling() {
    this.scroll.start();

    document.documentElement.style.setProperty('--scrollbar-width', `0px`);
  }

  getScrollbarWidth() {
    const container = document.createElement('div');
    document.body.appendChild(container);
    container.style.overflow = 'scroll';

    const inner = document.createElement('div');
    container.appendChild(inner);

    const scrollbarWidth = container.offsetWidth - inner.offsetWidth;

    container.parentNode?.removeChild(container);

    this.scrollbarWidth = scrollbarWidth;
  }

  scrollToSection($target: JQuery<HTMLElement>, centerInView = false) {
    const offset = $target.offset();
    if (!offset) return;
    let topOffset = offset.top;

    if (centerInView) {
      const height = $target.outerHeight() || 0;
      const windowHeight = window.innerHeight;
      topOffset = topOffset - windowHeight / 2 + height / 2;
    }

    this.scroll.scrollTo(topOffset, {
      offset: centerInView ? 0 : -150,
      duration: this.reducedMotion ? 0 : 1.5,
    });
  }
}

const SmoothScroll = new Scroll();
(window as any).SmoothScroll = SmoothScroll.scroll;

export default SmoothScroll;
